import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { useStyles } from '@wix/tpa-settings/react';

import {
  selectMessages,
  selectLayout,
  selectComposed,
  expandWidget,
  setRead as setReadThunk,
  getMessagesHistory,
  messagesQueryBuilder,
  selectOneChatComposed,
  selectOneChatSettings,
} from '@/features';
import { useAppDispatch, useAppSelector } from '@/store';
import { EXPERIMENTS, SS_ENGAGE_MESSAGE_SHOWN } from '@/constants';
import { useSessionStorage } from '@/hooks';

import {
  icons,
  iconsDict,
  SettingsMinimizedButtonPlayAnimation,
} from 'AiAssistantWidget/Settings/components';
import settingsParams from 'AiAssistantWidget/settingsParams';
import { useWidgetSettings } from 'AiAssistantWidget/Widget/contexts';
import styleParams from 'AiAssistantWidget/stylesParams';
import { useAppExperiments } from 'AiAssistantWidget/experiments';

import { NotificationBubble } from '../NotificationBubble';

import { MinimizedChatDataHooks } from './MinimizedChatDataHooks';
import { st, classes } from './MinimizedChat.st.css';

type PlayAnimationState = Record<
  SettingsMinimizedButtonPlayAnimation,
  boolean
> & {
  hovePreview: boolean;
};

const CANCEL_ANIMATION_TIMEOUT = 5000;

export const MinimizedChat = ({
  setRef,
}: {
  setRef: (ref: HTMLDivElement | null) => void;
}) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const settings = useWidgetSettings();
  const { experiments } = useAppExperiments();
  const dispatch = useAppDispatch();
  const { isMobile, isEditor } = useEnvironment();
  const lastUnread = useAppSelector(selectMessages.lastUnread);
  const isOneChat = experiments.enabled(EXPERIMENTS.ONE_CHAT);
  const isAi = useAppSelector(selectOneChatSettings.isAi);

  const isLegacyOnline = useAppSelector(selectComposed.maybeWidgetOnline);
  const isOneChatOnline = useAppSelector(
    selectOneChatComposed.maybeWidgetOnline,
  );
  const isOnline = isOneChat ? isOneChatOnline : isLegacyOnline;

  const shouldLoadMessages = useAppSelector(selectMessages.shouldLoadMessages);
  const wasOpened = useAppSelector(selectLayout.wasOpened);

  const legacyEngageMessage = useAppSelector(selectComposed.engageMessage);
  const oneChatEngageMessage = useAppSelector(
    selectOneChatComposed.engageMessage,
  );
  const engageMessage = isOneChat ? oneChatEngageMessage : legacyEngageMessage;

  const shouldAutoOpenLegacy = useAppSelector(
    selectComposed.shouldAutoOpenWidget,
  );
  const shouldAutoOpenOneChat = useAppSelector(
    selectOneChatComposed.shouldAutoOpenWidget,
  );
  const shouldAutoOpen = isOneChat
    ? shouldAutoOpenOneChat
    : shouldAutoOpenLegacy;

  const [engageMessageShown, setEngageMessageShown] = useSessionStorage(
    SS_ENGAGE_MESSAGE_SHOWN,
    false,
  );
  const isOpened = useAppSelector(selectLayout.visual) === 'visible';
  const buttonRef = useRef<HTMLButtonElement>(null);

  const hasLastUnread = useMemo<boolean>(
    () => Boolean(lastUnread),
    [lastUnread],
  );
  const hasEngageMessage = useMemo<boolean>(
    () =>
      Boolean(engageMessage) &&
      !engageMessageShown &&
      !shouldAutoOpen &&
      !isEditor,
    [engageMessage, engageMessageShown, isEditor, shouldAutoOpen],
  );

  const expand = useCallback(() => {
    if (hasEngageMessage) {
      setEngageMessageShown(true);
    }
    dispatch(expandWidget());
  }, [dispatch, hasEngageMessage, setEngageMessageShown]);

  const setRead = useCallback(() => {
    dispatch(setReadThunk());
  }, [dispatch]);

  const {
    textOnlineLegacy,
    textOfflineLegacy,
    textOnlineOneChat,
    textOfflineOneChat,
  } = useMemo(
    () => ({
      textOnlineLegacy: settingsParams.textButtonOnline,
      textOfflineLegacy: settingsParams.textButtonOffline,
      textOnlineOneChat: isAi
        ? settingsParams.textButtonOnline
        : settingsParams.manualChatTextButtonOnline,
      textOfflineOneChat: isAi
        ? settingsParams.textButtonOffline
        : settingsParams.manualChatTextButtonOffline,
    }),
    [isAi],
  );

  const buttonTextOnline = isOneChat ? textOnlineOneChat : textOnlineLegacy;
  const buttonTextOffline = isOneChat ? textOfflineOneChat : textOfflineLegacy;
  const buttonText = useMemo(
    () => settings.get(isOnline ? buttonTextOnline : buttonTextOffline),
    [settings, isOnline, buttonTextOnline, buttonTextOffline],
  );

  const [playAnimationStyleState, setPlayAnimationState] =
    useState<PlayAnimationState>({
      loop: false,
      load: false,
      hove: false,
      hovePreview: false,
    });

  const stylesConfig = useMemo(() => {
    let pos = 'floating';
    const settingsPos = settings.get(
      settingsParams.minimizedButtonPosition,
      'primary',
    );
    if (!isMobile && settingsPos) {
      pos = settingsPos;
    }

    let display = 'textIcon';
    const settingsDisplay = settings.get(
      settingsParams.minimizedButtonDisplay,
      'primary',
    );
    if (isMobile) {
      display = 'icon';
    } else if (settingsDisplay) {
      display = settingsDisplay;
    }

    const showBorderAnimation =
      settings.get(settingsParams.minimizedButtonBorderAnimation) &&
      styles.get(styleParams.minimizedButtonAnimatedBorderWidth) > 0;

    return {
      isLeft: settings.get(settingsParams.layout, 'primary') === 'left',
      isSticky: pos === 'sticky',
      showIcon: pos === 'icon' || display === 'icon' || display === 'textIcon',
      showText:
        pos !== 'icon' && (display === 'text' || display === 'textIcon'),
      hasShadow:
        settings.get(settingsParams.minimizedButtonShadow, 'primary') === true,
      icon:
        settings.get(settingsParams.minimizedButtonIcon, 'primary') ??
        icons[0].value,
      showBorderAnimation,
      playBorderAnimationType: settings.get(
        settingsParams.minimizedButtonPlayBorderAnimation,
      ),
      bubbleHasShadow:
        settings.get(settingsParams.bubbleNotificationShadow, 'primary') ===
        true,
    };
  }, [settings, styles, isMobile]);

  useEffect(() => {
    if (!isOpened && buttonRef.current) {
      buttonRef.current.focus();
    }
  }, [isOpened]);

  useEffect(() => {
    const loop = stylesConfig.playBorderAnimationType === 'loop';
    const load = stylesConfig.playBorderAnimationType === 'load';
    const hove = stylesConfig.playBorderAnimationType === 'hove';

    setPlayAnimationState((p) => ({
      ...p,
      loop,
      load,
      hove,
      hovePreview: isEditor && hove,
    }));

    if (load) {
      setTimeout(() => {
        setPlayAnimationState((p) => ({ ...p, load: false }));
      }, CANCEL_ANIMATION_TIMEOUT);
    }
  }, [stylesConfig, isEditor]);

  useEffect(() => {
    if (wasOpened && shouldLoadMessages) {
      if (shouldLoadMessages) {
        dispatch(getMessagesHistory(messagesQueryBuilder()));
      }
    }
  }, [wasOpened, shouldLoadMessages, dispatch]);

  const shouldShowPopover = (hasLastUnread || hasEngageMessage) && !isMobile;

  const textContent = useMemo(() => {
    if (lastUnread?.body === undefined) {
      return '';
    }

    const { mainText, additionalData } = lastUnread.body;

    if (mainText) {
      return mainText;
    }

    if (additionalData?.[0].fileName) {
      return t('app.widget.bubbleMessage.fileWithName', {
        fileName: additionalData?.[0].fileName,
      });
    }

    if (additionalData?.[0].type === 'media') {
      return t('app.widget.bubbleMessage.fileWithoutName');
    }

    return '';
  }, [lastUnread, t]);

  const unreadBubbleProps = {
    minWidth: 220,
    textContent,
    onClick: expand,
    onClose: setRead,
  };

  const engageBubbleProps = {
    minWidth: 150,
    textContent: engageMessage || '',
    onClick: expand,
    onClose: () => setEngageMessageShown(true),
  };

  const bubbleProps = hasLastUnread ? unreadBubbleProps : engageBubbleProps;

  return (
    <div
      className={st(classes.root, {
        left: stylesConfig.isLeft,
        sticky: stylesConfig.isSticky,
        nudge:
          settings.get(
            settingsParams.minimizedButtonNudgeAnimation,
            'primary',
          ) === true &&
          (!wasOpened || hasLastUnread),
        hasUnread: hasLastUnread,
        onlyIcon: stylesConfig.showIcon && !stylesConfig.showText,
      })}
      ref={setRef}
      data-hook={MinimizedChatDataHooks.ROOT}
    >
      {stylesConfig.showBorderAnimation && (
        <div
          className={st(classes.animatedBorderBox, playAnimationStyleState)}
        />
      )}
      <NotificationBubble
        shown={shouldShowPopover}
        placement={stylesConfig.isLeft ? 'top-start' : 'top-end'}
        hasShadow={stylesConfig.bubbleHasShadow}
        bubbleClassName={st(classes.bubble)}
        {...bubbleProps}
      >
        <button
          ref={buttonRef}
          onClick={expand}
          className={st(classes.button, {
            sticky: stylesConfig.isSticky,
            hasShadow: stylesConfig.hasShadow,
            showText: stylesConfig.showText,
          })}
          data-hook={MinimizedChatDataHooks.BUTTON}
          aria-expanded={isOpened}
          aria-label={`${t('app.widget.ariallabel.chatButton')}, ${
            hasLastUnread && t('app.widget.ariallabel.hasUnreadMessages')
          }, ${!isOnline && t('app.widget.ariallabel.chatUnavailable')}`}
        >
          {stylesConfig.showIcon && iconsDict[stylesConfig.icon]}
          {stylesConfig.showText && (
            <div className={st(classes.text)}>{buttonText}</div>
          )}
        </button>
      </NotificationBubble>
    </div>
  );
};
